import { useEffect, useState } from "react";
import type { ChatDTO } from "../../shared/types";
import { get } from "../lib/api";
import { Link } from "../lib/router";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import { Avatar, timeAgo } from "./ui";
import { realtime } from "../lib/ws";

const FOLDED_KEY = "agentos.chat-sidebar-folded";

const readFolded = () => {
  try {
    return localStorage.getItem(FOLDED_KEY) === "1";
  } catch {
    return false;
  }
};
const storeFolded = (folded: boolean) => {
  try {
    if (folded) localStorage.setItem(FOLDED_KEY, "1");
    else localStorage.removeItem(FOLDED_KEY);
  } catch {
    // storage unavailable
  }
};

/** The time a row is ranked by, so the list reads in order. A chat they never wrote in shows its last message. */
const rowTime = (c: ChatDTO) => c.lastTalkedAt ?? c.lastMessageAt;

/**
 * The rows: one per agent, Pandito first, then the one the person last wrote to. Only an admin has a list to choose
 * from; everyone else reaches a specialist through Pandito. There is one chat per agent, so there is nothing to start.
 */
export function AgentList({ chats, activeAgentId, onNavigate }: { chats: ChatDTO[]; activeAgentId?: string; onNavigate?: () => void }) {
  return (
    <div className="scroll-thin flex-1 space-y-0.5 overflow-auto px-2 py-3">
      {chats.map((c) => (
        <div key={c.agentId}>
          <Link
            to={`/a/${c.slug}`}
            onClick={onNavigate}
            className={`flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm transition-colors ${
              c.agentId === activeAgentId
                ? "bg-fill font-medium text-fg"
                : "text-muted hover:bg-fill-subtle hover:text-fg"
            }`}
          >
            <Avatar name={c.name} src={c.avatarUrl} />
            <span className="min-w-0 flex-1">
              <span className="block truncate">{c.name}</span>
              <span className="block truncate text-xs text-faint">{rowTime(c) ? timeAgo(rowTime(c)!) : "No messages yet"}</span>
            </span>
          </Link>
        </div>
      ))}
      {chats.length === 0 && <p className="px-3 text-xs text-faint">No agents to talk to yet.</p>}
    </div>
  );
}

/** The sidebar on a computer: the rows, folded down to the one button that brings them back, as this browser left it. */
export function AgentSidebar({ chats, activeAgentId }: { chats: ChatDTO[]; activeAgentId?: string }) {
  const [folded, setFolded] = useState(readFolded);
  const fold = (next: boolean) => {
    setFolded(next);
    storeFolded(next);
  };
  if (folded) {
    return (
      <aside className="hidden w-13 shrink-0 flex-col items-center border-r border-line bg-surface py-2.5 md:flex">
        <IconButton label="Show agents" fill="none" onClick={() => fold(false)}>
          <Icon name="sidebar" />
        </IconButton>
      </aside>
    );
  }
  return (
    <aside className="hidden w-64 shrink-0 flex-col border-r border-line bg-surface md:flex">
      <div className="flex items-center justify-between pt-2.5 pr-2 pl-4">
        <span className="text-sm font-semibold">Agents</span>
        <IconButton label="Hide agents" fill="none" onClick={() => fold(true)}>
          <Icon name="sidebar" />
        </IconButton>
      </div>
      <AgentList chats={chats} activeAgentId={activeAgentId} />
    </aside>
  );
}

/** The sidebar's rows, kept up to date while the open chat is being answered. */
export function useChats(activeId?: string) {
  const [chats, setChats] = useState<ChatDTO[]>([]);
  const load = () => get<ChatDTO[]>("/api/chats").then(setChats, () => {});
  useEffect(() => {
    load();
  }, [activeId]);
  useEffect(() => {
    if (!activeId) return;
    return realtime.subscribe(`conversation:${activeId}`, (e) => {
      if (e.type === "message.created") load();
    });
  }, [activeId]);
  return chats;
}
